import React, { useEffect, useMemo, useState } from 'react'
import PasswordGate from '../PasswordGate'
import { isAuthedClient } from '../../utils/auth'
import { RichText } from 'prismic-reactjs'
import groupBy from 'lodash/groupBy'
import sortBy from 'lodash/sortBy'
import get from 'lodash/get'
import styled from 'styled-components'
import ClientList from '../ClientList'
import { openLinksNewTab } from '../../utils/helpers'
import Grid from '../Grid'
import SiteBackgroundVideo from '../SiteBackgroundVideo'
import { getVariantField, hasVariantField, isPublicVariant } from '../../utils/siteVariant'
import { filterClientsForCurrentVariant } from '../../utils/contentTargeting'
import {
  getThemeBackground,
  getThemeColor,
  isLightTheme,
} from '../../utils/siteTheme'

const StyledPage = styled.div`
  opacity: 1;
  width: 100vw;
  min-height: 100vh;
  overflow-x: hidden;

  &.has-background-video {
    background: transparent;

    .mobile-stack,
    .mobile-title {
      background: transparent;
    }

    .mobile-stack {
      position: relative;
      z-index: 1;
    }
  }
`

const StyledMobileStack = styled.div`
  background: ${(props) => props.background};
  color: ${(props) => props.color};
  font-family: 'Phonic Regular', 'Helvetica Neue', sans-serif;
  min-height: 100vh;
  padding: 0 8px 24px;
  width: 100vw;

  .mobile-section {
    border-top: 1px solid ${(props) => props.color};
    padding: 8px 0;
  }

  .border-top {
    border-top: 1px solid ${(props) => props.color};
    padding: 8px 0;
  }

  .mobile-title {
    padding: 11px 0;
  }

  .mobile-nav {
    .client {
      padding-left: 0.5rem;
    }

    .project {
      padding-left: 1rem;
    }
  }

  .mobile-work-section {
    padding-top: 8px;
  }

  .mobile-work-heading {
    border-top: 1px solid ${(props) => props.color};
    padding: 8px 0;
  }

  .grid-wrapper {
    display: block;
  }

  .grid-item {
    margin-bottom: 24px;
  }

  .grid-item.mobile-fade-entry {
    opacity: 0;
    transform: translateY(18px);
    transition: opacity 520ms ease, transform 520ms ease;
    will-change: opacity, transform;
  }

  .grid-item.mobile-fade-entry.is-visible {
    opacity: 1;
    transform: translateY(0);
  }

  .grid-item img {
    display: block;
    width: 100%;
  }

  .project-info {
    margin-bottom: 16px;
  }

  a {
    color: inherit;
  }

  @media (prefers-reduced-motion: reduce) {
    .grid-item.mobile-fade-entry {
      opacity: 1;
      transform: none;
      transition: none;
      will-change: auto;
    }
  }
`

function FrontPageMobile({ pageData }) {
  const bio = pageData[0]

  const results = get(pageData, '[1].results', [])
  const groups = results.length > 0 ? groupBy(results, 'data.category') : {}

  const siteSettings = get(pageData[2], 'results[0].data', {})
  const bioText = getVariantField(get(bio, 'data', {}), 'bio')
  const hasVariantBio = hasVariantField(get(bio, 'data', {}), 'bio')
  const aboutBackground = getThemeBackground(siteSettings)
  const aboutText = getThemeColor(siteSettings)
  const lightTheme = isLightTheme(siteSettings)
  const siteBackgroundVideoUrl = get(siteSettings, 'site_background_video_link.url', '')
  const useSiteBackgroundVideo = !isPublicVariant() && !!siteBackgroundVideoUrl
  const themeClassName = lightTheme ? ' theme-light' : ''
  const pageClassName = `page-container${themeClassName}${
    useSiteBackgroundVideo ? ' has-background-video' : ''
  }`
  const stackBackground = useSiteBackgroundVideo ? 'transparent' : aboutBackground

  const [authed, setAuthed] = useState(() => isAuthedClient())

  useEffect(() => {
    openLinksNewTab()
  }, [authed])

  const getClientNameKey = (client) => {
    const name = RichText.asText(get(client, 'data.group_name', [])) || ''
    return name.trim().toLowerCase()
  }

  const { works, arts } = useMemo(() => {
    const worksRaw = get(groups, 'Campaign', [])
    const brandRaw = [
      ...get(groups, 'Digital', []),
      ...get(groups, 'Brand', []),
    ]

    return {
      works: sortBy(filterClientsForCurrentVariant(worksRaw), getClientNameKey),
      arts: sortBy(filterClientsForCurrentVariant(brandRaw), getClientNameKey),
    }
  }, [groups])

  useEffect(() => {
    if (!authed) return undefined
    if (typeof window === 'undefined') return undefined

    const items = Array.from(document.querySelectorAll('.mobile-stack .grid-item'))
    if (!items.length) return undefined

    const reduceMotion =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches

    if (reduceMotion || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('mobile-fade-entry', 'is-visible'))
      return undefined
    }

    items.forEach((item) => item.classList.add('mobile-fade-entry'))

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          entry.target.classList.add('is-visible')
          observer.unobserve(entry.target)
        })
      },
      {
        rootMargin: '0px 0px -8% 0px',
        threshold: 0.08,
      },
    )

    items.forEach((item) => observer.observe(item))

    return () => observer.disconnect()
  }, [authed, works, arts])

  const handleProjectClick = (columnClass, wayfinderId) => {
    const elmt = document.getElementById(wayfinderId)
    if (!elmt) return

    try {
      elmt.scrollIntoView({ behavior: 'smooth', block: 'start' })
    } catch (e) {}
  }

  if (!authed) {
    return (
      <StyledPage className={pageClassName}>
        {useSiteBackgroundVideo && (
          <SiteBackgroundVideo
            src={siteBackgroundVideoUrl}
            light={lightTheme}
            showOverlay={false}
          />
        )}

        <StyledMobileStack
          className="mobile-stack"
          background={stackBackground}
          color={aboutText}
        >
          <p className="mobile-title">
            <span className="first">Jordan</span>
            <span className="last"> Philips</span>
          </p>

          <div className={`mobile-section bio${hasVariantBio ? ' variant-bio' : ''}`}>
            {<RichText render={bioText} />}
          </div>

          <div className="mobile-section clients">
            {<RichText render={bio.data.select_clients} />}
          </div>

          <div className="mobile-section clients">
            {<RichText render={bio.data.site_credit} />}
          </div>

          <PasswordGate
            textColor={aboutText}
            expectedPassword={get(siteSettings, 'site_password', '')}
            onSuccess={() => setAuthed(true)}
          />
        </StyledMobileStack>
      </StyledPage>
    )
  }

  return (
    <StyledPage className={pageClassName}>
      {useSiteBackgroundVideo && (
        <SiteBackgroundVideo src={siteBackgroundVideoUrl} light={lightTheme} />
      )}

      <StyledMobileStack
        className="mobile-stack"
        background={stackBackground}
        color={aboutText}
      >
        <p className="mobile-title">
          <span className="first">Jordan</span>
          <span className="last"> Philips</span>
        </p>

        <div className={`mobile-section bio${hasVariantBio ? ' variant-bio' : ''}`}>
          {<RichText render={bioText} />}
        </div>

        <div className="mobile-work-section column-work">
          <Grid clients={works} playerTheme={'red'} />
        </div>

        <div className="mobile-work-section column-art">
          <Grid clients={arts} playerTheme={'blue'} />
        </div>

        <div className="mobile-section mobile-nav">
          <div className="client-work">
            <p>Campaign</p>
            <ClientList
              clients={works}
              handleProjectClick={handleProjectClick}
              columnClass="column-work"
            />
          </div>

          <div className="client-art">
            <p>Brand</p>
            {arts.length === 0 ? (
              <div className="client" style={{ paddingLeft: '0.5rem', opacity: 0.75 }}>
                No Brand projects found.
              </div>
            ) : (
              <ClientList
                clients={arts}
                handleProjectClick={handleProjectClick}
                columnClass="column-art"
              />
            )}
          </div>
        </div>

        <div className="mobile-section clients">
          {<RichText render={bio.data.select_clients} />}
        </div>

        <div className="mobile-section award">
          {<RichText render={get(bio, 'data.awards')} />}
        </div>

        <div className="mobile-section award">
          {<RichText render={get(bio, 'data.site_credit')} />}
        </div>
      </StyledMobileStack>
    </StyledPage>
  )
}

export default FrontPageMobile
